import axios from 'axios';
import { createAuthClient } from './authClient';
import { API_CONFIG } from './api-config';

// Use singleton auth client instance
let authClient = createAuthClient();

// API base URL from configuration
const API_BASE_URL = API_CONFIG.CAREER_FEED_API_URL;

// Create axios instance
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Configure the API client with auth client
export const configureApiClient = (authClient) => {
  console.log('🔧 Configuring API client with authClient:', authClient);
  
  // Request interceptor to add authorization header
  apiClient.interceptors.request.use(
    async (config) => {
      console.log('🔧 Request interceptor called for:', config.url);
      
      // If the request already has an Authorization header, return the config
      if (config.headers.Authorization) {
        console.log('🔧 Request already has Authorization header');
        return config;
      }
      
      // Get a valid access token from authClient (handles refresh automatically)
      console.log('🔧 Getting valid token from authClient...');
      const accessToken = await authClient.getValidToken();
      console.log('🔧 Got token:', accessToken ? 'YES' : 'NO');
      
      if (accessToken) {
        config.headers.Authorization = `Bearer ${accessToken}`;
        console.log('🔐 API Request to:', config.url, 'with token:', `Bearer ${accessToken.substring(0, 20)}...`);
      } else {
        // If getValidToken returns null, it means user was redirected to login
        console.warn('🔐 No valid token available for request to:', config.url);
      }
      
      return config;
    },
    (error) => {
      console.error('🔧 Request interceptor error:', error);
      return Promise.reject(error);
    }
  );

  // Response interceptor - simplified since authClient handles token refresh
  apiClient.interceptors.response.use(
    (response) => {
      // Broadcast AI usage updates to shell app header
      if (response.data?.aiUsage) {
        window.dispatchEvent(new CustomEvent('aiUsageUpdate', { detail: response.data.aiUsage }));
      }
      return response;
    },
    async (error) => {
      const originalRequest = error.config;
      
      // Handle 429 errors with usage limit information
      if (error.response?.status === 429) {
        console.log('🔐 Received 429 error:', error.response.data);
        
        // Check if the response contains usage limit details (backend returns 'usageDetails')
        if (error.response.data?.usageDetails) {
          const usageDetails = error.response.data.usageDetails;
          console.log('🔐 Usage limit exceeded:', usageDetails);
          
          // Dispatch global event for usage limit handling
          window.dispatchEvent(new CustomEvent('usageLimitExceeded', {
            detail: {
              usageDetails,
              context: 'AI enhancement'
            }
          }));
        } else {
          console.log('🔐 429 error but not usage limit format - dispatching anyway');
          // Still dispatch event even if format is different
          window.dispatchEvent(new CustomEvent('usageLimitExceeded', {
            detail: {
              usageDetails: {
                used: 'Unknown',
                limit: 'Unknown'
              },
              context: 'AI enhancement'
            }
          }));
        }
        
        return Promise.reject(error);
      }
      
      // Handle 401 errors - token refresh is handled by request interceptor
      // Only sign out if we're already on login page (refresh failed)
      if (error.response?.status === 401) {
        console.error('🔐 Received 401 error after token refresh attempt');
        if (window.location.pathname !== '/login') {
          window.location.href = '/login';
        }
        return Promise.reject(error);
      }
      
      return Promise.reject(error);
    }
  );
};

// Auto-configure interceptors with auth client AFTER function definition
if (authClient && typeof authClient.getValidToken === 'function') {
  configureApiClient(authClient);
} else {
  console.error('❌ AuthClient not properly initialized:', authClient);
}

// Helper function to make authenticated requests using the configured apiClient with interceptors
export const makeAuthenticatedRequest = async (url, method = 'GET', data = null, options = {}) => {
  console.log('🎯 makeAuthenticatedRequest CALLED:', { url, method, hasData: !!data, options });
  
  // Determine which API base URL to use based on apiType option
  let baseURL = API_BASE_URL; // Default to Career Feed API
  if (options.apiType === 'reports') {
    baseURL = API_CONFIG.REPORTS_API_URL;
  } else if (options.apiType === 'bedrock') {
    baseURL = BEDROCK_API_BASE_URL;
  }
  
  console.log('🎯 Using baseURL:', baseURL);
  
  // Use the configured apiClient instance (which has interceptors) instead of creating temp instance
  const config = {
    method,
    url: `${baseURL}${url}`,
    ...(data && { data })
  };
  
  console.log('🎯 About to call apiClient with config:', config);
  const response = await apiClient(config);
  console.log('🎯 apiClient response received:', response.status);
  return response.data;
};

export default apiClient;

// Bedrock Enhancement API Base URL from configuration
const BEDROCK_API_BASE_URL = API_CONFIG.BEDROCK_API_URL;

/**
 * Enhance card using Bedrock AI
 */
export const enhanceCard = async (cardData, cardType, enhancementType) => {
  console.log('🤖 enhanceCard CALLED:', { cardType, enhancementType });
  
  const requestBody = { cardData, cardType, enhancementType };
  const endpoint = `/enhance/${cardType}`;
  
  console.log('🤖 About to call makeAuthenticatedRequest with endpoint:', endpoint);
  return await makeAuthenticatedRequest(endpoint, 'POST', requestBody, { apiType: 'bedrock' });
};

/**
 * Generic card enhancement (fallback)
 */
export const enhanceCardGeneric = async (cardData, cardType, enhancementType) => {
  console.log('🤖 enhanceCardGeneric: Starting generic enhancement request');

  const requestBody = {
    cardData,
    cardType,
    enhancementType
  };

  try {
    // Get a valid token from authClient (handles refresh/redirect automatically)
    const token = await authClient.getValidToken();
    
    if (!token) {
      console.log('🤖 enhanceCardGeneric: No valid token, user redirected to login');
      return;
    }

    const endpoint = `${BEDROCK_API_BASE_URL}/enhance/card`;
    
    const response = await axios({
      method: 'POST',
      url: endpoint,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      data: requestBody
    });
    
    console.log('🤖 enhanceCardGeneric: Enhancement successful:', response.data);
    return response.data;
    
  } catch (error) {
    console.error('🤖 enhanceCardGeneric: Enhancement failed:', error);
    
    // If 401 error, let authClient handle it
    if (error.response?.status === 401) {
      console.error('🤖 enhanceCardGeneric: 401 error - redirecting to login');
      authClient.signOut();
      window.location.href = '/login';
      return;
    }
    
    throw error;
  }
};
